import { useRef, useState } from "react";
import { Link } from "../lib/router";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useMeta } from "../hooks/useMeta";
import { EASE, Marquee, MaskedLines, Overline, Reveal } from "../components/motion";
import { PButton } from "../components/forms";
import { EditionSelector, ProductStage } from "../components/editions";
import { DEFAULT_EDITION, REFORMERS, findEdition } from "../data/products";

const EditionsSection = ({ t, lang }) => {
  const [edition, setEdition] = useState(DEFAULT_EDITION);
  const ed = findEdition(edition);
  return (
    <section data-testid="home-editions" className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40 lg:px-20">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal y={14}>
            <Overline className="mb-6">{t.home.editionsOverline}</Overline>
          </Reveal>
          <h2 data-testid="home-editions-title" className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-[#1C1C1A] md:text-6xl">
            <MaskedLines lines={t.home.editionsTitle} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-sm text-base font-light leading-relaxed text-[#66625A]">{t.home.editionsText}</p>
            <div className="mt-10">
              <EditionSelector value={edition} onChange={setEdition} testPrefix="home-edition" />
            </div>
            <p data-testid="home-edition-name" className="mt-8 font-serif text-2xl font-light text-[#1C1C1A]">{ed.label[lang]}</p>
            <div className="mt-8">
              <PButton to={`/reformer?edition=${edition}`} variant="outline" data-testid="home-editions-cta">{t.home.reformerCta}</PButton>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <ProductStage product={REFORMERS[0]} edition={edition} testId="home-editions-stage" className="aspect-[4/3] p-8 md:p-14" imgClassName="drop-shadow-[0_30px_40px_rgba(0,0,0,0.18)]" />
        </Reveal>
      </div>
    </section>
  );
};

const StudiosSection = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section data-testid="home-studios" className="bg-[#F3EFEA]">
      <div ref={ref} className="relative h-[60vh] min-h-[380px] overflow-hidden bg-[#1C1C1A] md:h-[80vh]">
        <motion.img src="/images/products/studio-ivory.jpg" alt="Pilates Studio mit Reformern von PILATES ON." style={{ y }} className="absolute -top-[10%] left-0 h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-[#1C1C1A]/35" />
        <div className="absolute inset-x-0 bottom-0 z-10 mx-auto w-full max-w-[1400px] px-6 pb-12 md:px-12 md:pb-20 lg:px-20">
          <Reveal>
            <Overline light className="mb-6">{t.home.studiosOverline}</Overline>
          </Reveal>
          <h2 data-testid="home-studios-title" className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-[#FAF8F5] md:text-7xl lg:text-8xl">
            <MaskedLines lines={t.home.studiosTitle} />
          </h2>
        </div>
      </div>
      <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-20 md:px-12 md:py-28 lg:grid-cols-12 lg:gap-16 lg:px-20">
        <div className="lg:col-span-5">
          <Reveal>
            <p data-testid="home-studios-text" className="text-lg font-light leading-relaxed text-[#1C1C1A] md:text-xl">{t.home.studiosText}</p>
            <ul data-testid="home-audiences" className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#66625A]">
              {t.studios.audiences.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <PButton to="/fuer-studios" variant="solid" data-testid="home-studios-cta">{t.home.studiosCta}</PButton>
              <Link to="/fuer-studios" data-testid="home-studios-link" className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1C1C1A]">
                <span className="border-b border-[#1C1C1A]/30 pb-1 transition-colors duration-300 group-hover:border-[#C5A059] group-hover:text-[#C5A059]">{t.home.studiosLink}</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1.5" />
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="grid gap-10 lg:col-span-6 lg:col-start-7">
          {t.studios.focus.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.1}>
              <div data-testid={`home-benefit-${i + 1}`} className="grid grid-cols-[3rem_1fr] gap-4 border-t border-[#1C1C1A]/15 pt-6">
                <span className="font-serif text-3xl font-light italic text-[#C5A059]">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1C1C1A]">{f.t}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-[#66625A]">{f.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t, lang } = useLanguage();
  useMeta(t.seo.home[0], t.seo.home[1]);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} data-testid="home-hero" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-[#1C1C1A]">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src="/images/hero.jpg" alt="Pilates Reformer in einem hellen, architektonischen Studio" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-[#1C1C1A]/40" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#1C1C1A]/70 to-transparent" />

        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-24 md:px-12 md:pb-32 lg:px-20">
          <h1 data-testid="hero-title" className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-[#FAF8F5] md:text-7xl lg:text-8xl">
            <MaskedLines lines={t.home.heroLines} mount delay={0.35} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: EASE }}
            data-testid="hero-sub"
            className="mt-8 font-mono text-[11px] uppercase tracking-[0.35em] text-[#DFCA9B]"
          >
            {t.home.heroSub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <PButton to="/fuer-studios" variant="light" data-testid="hero-cta-studios">{t.home.heroCtaPrimary}</PButton>
            <PButton to="/anfrage" variant="outlineLight" data-testid="hero-cta-quote">{t.home.heroCtaSecondary}</PButton>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 md:right-12 lg:right-20 md:flex"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FAF8F5]/60 [writing-mode:vertical-rl]">{t.home.scroll}</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} className="block h-12 w-px bg-[#C5A059]" />
        </motion.div>
      </section>

      <Marquee items={t.home.marquee} />

      <StudiosSection t={t} />

      <section data-testid="home-philosophy" className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-44 lg:px-20">
        <Reveal y={14}>
          <Overline className="mb-10">{t.home.philosophyOverline}</Overline>
        </Reveal>
        <div className="grid gap-12 lg:grid-cols-12">
          <h2 data-testid="home-philosophy-title" className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#1C1C1A] md:text-6xl lg:col-span-6">
            <MaskedLines lines={t.home.philosophyTitle} />
          </h2>
          <div className="flex flex-col justify-end lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.2}>
              <p data-testid="home-philosophy-lead" className="font-serif text-2xl font-light italic leading-snug text-[#1C1C1A] md:text-3xl">{t.home.philosophyLead}</p>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#66625A]">{t.home.philosophyText}</p>
              <Link to="/ueber-uns" data-testid="home-philosophy-link" className="group mt-8 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#1C1C1A]">
                <span className="border-b border-[#1C1C1A]/30 pb-1 transition-colors duration-300 group-hover:border-[#C5A059] group-hover:text-[#C5A059]">{t.home.philosophyLink}</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="home-reformer" className="bg-[#F3EFEA]">
        <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-6 py-28 md:px-12 md:py-40 lg:grid-cols-12 lg:px-20">
          <div className="relative lg:col-span-7">
            <div className="absolute -left-6 -top-6 hidden h-full w-full bg-[#E8E2D8] md:block" />
            <Reveal className="relative">
              <Link to="/reformer" data-testid="home-reformer-image" className="group block">
                <ProductStage product={REFORMERS[1]} edition="natural" testId="home-reformer-stage" className="aspect-[4/3] p-8 md:p-12" imgClassName="transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03] drop-shadow-[0_24px_36px_rgba(0,0,0,0.16)]" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal y={14}>
              <Overline className="mb-6">{t.home.reformerOverline}</Overline>
            </Reveal>
            <h2 data-testid="home-reformer-title" className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#1C1C1A] md:text-6xl">
              <MaskedLines lines={t.home.reformerTitle} />
            </h2>
            <Reveal delay={0.3}>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#66625A] md:text-lg">{t.home.reformerText}</p>
              <div className="mt-10">
                <PButton to="/reformer" variant="solid" data-testid="home-reformer-cta">{t.home.reformerCta}</PButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <EditionsSection t={t} lang={lang} />

      <section data-testid="home-cta" className="bg-[#1C1C1A] text-[#FAF8F5]">
        <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40 lg:px-20">
          <Reveal y={14}>
            <Overline light className="mb-8">{t.home.ctaOverline}</Overline>
          </Reveal>
          <h2 data-testid="home-cta-title" className="font-serif text-4xl font-light leading-[1.05] tracking-tight md:text-6xl">
            <MaskedLines lines={t.home.ctaTitle} />
          </h2>
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-[#FAF8F5]/70 md:text-lg">{t.home.ctaText}</p>
            <div className="mt-10">
              <PButton to="/anfrage" variant="light" data-testid="home-cta-button">{t.home.ctaBtn}</PButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
